import React from "react";
import { Grid, Box, Typography } from "@mui/material";
import DocumentMeta from "react-document-meta";
import useMediaQuery from "@mui/material/useMediaQuery";
import Matto1 from "./common/material/matto1.jpg";
import Matto2 from "./common/material/matto2.jpg";
import Matto3 from "./common/material/matto3.jpg";
import Matto4 from "./common/material/matto4.jpg";
import Matto5 from "./common/material/matto5.jpg";
import Matto6 from "./common/material/matto6.jpg";
import Matto7 from "./common/material/matto7.jpg";
import Matto8 from "./common/material/matto8.jpg";
import Tobibako1 from "./common/material/tobibako1.jpg";
import Tobibako2 from "./common/material/tobibako2.jpg";
import Tobibako3 from "./common/material/tobibako3.jpg";
import Tobibako4 from "./common/material/tobibako4.jpg";
import Tobibako5 from "./common/material/tobibako5.jpg";
import Tetubou1 from "./common/material/tetubou1.jpg";
import Tetubou2 from "./common/material/tetubou2.jpg";
import Core1 from "./common/material/core1.jpg";
import Core2 from "./common/material/core2.jpg";
import Core3 from "./common/material/core3.jpg";
import Toranporin3 from "./common/material/toranporin3.jpg";
import { RegionId } from "./common/branches";

export interface BlogPostSeo {
  title?: string; // 未指定なら title を使用
  description?: string; // 未指定なら excerpt を使用
  keywords?: string[]; // 記事固有のSEOキーワード(共通キーワードに追加)
}

export type BlogContentBlock =
  | { type: "paragraph"; text: string }
  | { type: "heading"; text: string }
  | { type: "note"; text: string }
  | { type: "table"; caption?: string; headers: string[]; rows: string[][] }
  | { type: "link"; text: string; href: string }
  | { type: "cta"; text: string; href: string; external?: boolean }
  | { type: "faq"; items: { q: string; a: string }[] };

export interface BlogPostData {
  id: string;
  title: string;
  date: string;
  excerpt: string;
  image: string;
  imageAlt?: string; // 未指定なら title を使用(サムネイル/OGP画像のalt)
  content: BlogContentBlock[];
  seo?: BlogPostSeo;
  // 店舗固有記事の場合はRegionIdを指定(店舗別ブログ /:region/blog に表示される)。
  // 全社共通記事の場合は "shared"。既存の地域横断まとめ記事のように
  // 特定の1店舗に属さない場合は未指定のままでよい(/blog 一覧にのみ表示)。
  storeId?: RegionId | "shared";
}

// 全記事共通のSEOキーワード。記事ごとの seo.keywords はこれに追加される。
export const commonBlogKeywords = ["土日だけ体操教室", "ブログ", "体操教室"];

// post.date は "2026年9月21日" のような日本語表記の文字列で、
// 文字列としては正しい日付順に並ばない(例:"9日" > "21日")ため、
// 数値化してから比較する。パースできない形式は最後尾(0)に回す。
const parseJaDate = (jaDate: string): number => {
  const m = jaDate.match(/(\d+)年(\d+)月(\d+)日/);
  if (!m) return 0;
  const [, y, mo, d] = m;
  return Number(y) * 10000 + Number(mo) * 100 + Number(d);
};

// 日付が新しい記事を先頭にした配列を返す(元の配列は変更しない)。
// ブログ一覧(全社共通・店舗別)はすべてこの並び順で表示する。
export const sortPostsByDateDesc = (posts: BlogPostData[]): BlogPostData[] =>
  [...posts].sort((a, b) => parseJaDate(b.date) - parseJaDate(a.date));

export const blogPosts: BlogPostData[] = [
  {
    id: "ishikawa-gym-guide-2026",
    title: "石川県の体操教室6選【2026年最新】金沢・小松のおすすめまとめ",
    date: "2026年7月21日",
    excerpt:
      "石川県で体操教室を探すなら必見。金沢市(ココナスポーツクラブ・畝田・工大前・駅西・城北)から小松市まで、教室ごとの特徴とクラスを一挙に紹介します。",
    image: Tobibako2,
    content: [
      {
        type: "paragraph",
        text: "石川県には金沢市を中心に、地域や特徴の異なる体操教室が複数あります。この記事では石川県内の体操教室を教室ごとに、特徴・対象年齢・クラスまで詳しくまとめました。お子さまに合う教室選びの参考にしてください。",
      },

      { type: "heading", text: "ココナスポーツクラブ【金沢市】" },
      {
        type: "paragraph",
        text: "いしかわ総合スポーツセンターや金沢市総合体育館など、複数の会場で開催している石川県最大級規模の体操教室です。曜日・体育館を自由に選べる「フリー曜日・フリー体育館制」が特徴で、部活動や他の習い事と両立しやすいのが魅力です。1歳から無料体験を受け付けています。",
      },
      {
        type: "table",
        caption: "クラス一覧",
        headers: ["クラス"],
        rows: [
          ["ファミリー(親子)"],
          ["キッズ／ジュニア初級／チアキッズ"],
          ["チアジュニア／ジュニア"],
          ["クッション"],
          ["タンブリング"],
          ["アスリート"],
          ["育成"],
          ["チア育成ブルー"],
        ],
      },
      { type: "cta", text: "ココナスポーツクラブの公式サイトを見る", href: "https://coconasportsclub.com/", external: true },

      { type: "heading", text: "畝田体操教室【金沢市】" },
      {
        type: "paragraph",
        text: "石川県金沢市畝田西２丁目７−１にある教室です。ココナスポーツクラブとは対照的に「週ごとの固定クラス」制を採用しており、毎週同じ曜日・時間・仲間とじっくり取り組めるのが特徴です。1歳から無料体験を受け付けています。",
      },
      {
        type: "table",
        caption: "クラス一覧",
        headers: ["クラス"],
        rows: [
          ["ファミリー(親子)"],
          ["キッズ／ジュニア初級／チアキッズ"],
          ["チアジュニア／ジュニア"],
          ["クッション"],
          ["タンブリング"],
          ["アスリート"],
          ["育成"],
        ],
      },
      { type: "cta", text: "畝田体操教室の公式サイトを見る", href: "https://naoe-taisou.jimdofree.com/", external: true },

      { type: "heading", text: "工大前教室【金沢市】" },
      {
        type: "paragraph",
        text: "金沢工業大学付近で開催している教室です。ココナスポーツクラブ・畝田体操教室と同じクラス体系で、育成クラスからチア、タンブリングまで幅広いプログラムに対応しています。",
      },
      {
        type: "table",
        caption: "クラス一覧",
        headers: ["クラス"],
        rows: [
          ["キッズ／ジュニア初級／チアキッズ"],
          ["チアジュニア／ジュニア"],
          ["クッション"],
          ["タンブリング"],
          ["アスリート"],
        ],
      },
      { type: "cta", text: "工大前教室(パール)の公式サイトを見る", href: "https://pearl-taisou.jimdofree.com/", external: true },

      { type: "heading", text: "駅西教室【金沢市】" },
      {
        type: "paragraph",
        text: "金沢駅西口エリアで開催している教室です。金沢駅からのアクセスがよく、平日夕方や土日に通いやすい立地が魅力です。",
      },
      {
        type: "table",
        caption: "クラス一覧",
        headers: ["クラス"],
        rows: [
          ["キッズ／ジュニア初級／チアキッズ"],
          ["チアジュニア／ジュニア"],
          ["クッション"],
          ["タンブリング"],
        ],
      },
      { type: "cta", text: "運営元(ココナスポーツクラブ)の公式サイトを見る", href: "https://coconasportsclub.com/", external: true },

      { type: "heading", text: "金沢市城北体操教室【城北・石川工大前】" },
      {
        type: "paragraph",
        text: "石川県金沢市鳴和にある教室で、城北・石川工大前の2拠点で開催しています。年少〜年中向けの「体操・トランポリン・体づくり運動・かけっこ」から、中学生・高校生・大人まで通える「本格的な体幹トレーニング」まで、年齢に応じて段階的にクラスが分かれているのが特徴です。",
      },
      {
        type: "table",
        caption: "クラス一覧",
        headers: ["クラス", "対象"],
        rows: [
          ["体操・トランポリン・体づくり運動・かけっこ", "3歳〜5歳"],
          ["体操・トランポリン・体づくり運動・かけっこ", "5歳〜小学生"],
          ["体操・トランポリン・体幹トレーニング・陸上", "小学生"],
          ["体操・バク転", "小学生"],
          ["本格的な体幹トレーニング", "中学生・高校生・大人"],
        ],
      },
      { type: "cta", text: "金沢市城北体操教室の教室ページを見る", href: "/ishikawa" },

      { type: "heading", text: "小松体操教室【小松市】" },
      {
        type: "paragraph",
        text: "石川県小松市光町にある教室です。土日開催で、幼児から小学生まで通いやすいクラス編成になっています。クッションクラスの前段階として「キッズバク転」クラスも用意されています。",
      },
      {
        type: "table",
        caption: "クラス一覧",
        headers: ["クラス", "対象"],
        rows: [
          ["キッズ", "幼児5歳〜小学2年生"],
          ["ジュニア", "小学生"],
          ["クッション・バク転", "小学生〜"],
          ["キッズバク転", "幼児5歳〜小学2年生"],
        ],
      },
      { type: "cta", text: "小松体操教室の教室ページを見る", href: "/komatsu" },

      { type: "heading", text: "石川県の体操教室選びでよくある質問" },
      {
        type: "faq",
        items: [
          {
            q: "石川県の体操教室は何歳から通えますか?",
            a: "教室によって異なりますが、多くの教室で1歳からの親子クラスや3歳〜5歳の幼児クラスを用意しています。年齢に不安がある場合は、まず無料体験でお子さまの様子を見てもらうのがおすすめです。",
          },
          {
            q: "石川県内で体操教室を選ぶポイントは?",
            a: "自宅や学校から通いやすい立地か、クラスの曜日・時間が生活リズムに合うか、少人数制かフリー制かなど教室ごとの指導スタイルをチェックするのがポイントです。金沢市内には複数の教室があるため、比較検討してみてください。",
          },
          {
            q: "運動が苦手でも体操教室に通えますか?",
            a: "はい、多くの教室で運動が苦手なお子さま向けのクラスやプログラムを用意しています。少人数制やレベルに応じた指導を行っている教室も多いので、まずは無料体験でご相談ください。",
          },
        ],
      },
      {
        type: "paragraph",
        text: "教室ごとにクラス体系や特徴が異なるため、お住まいの地域から通いやすい教室をチェックした上で、まずは無料体験にお申し込みいただくのがおすすめです。",
      },
      {
        type: "link",
        text: "北陸(石川・富山・福井)の体操教室まとめ記事も見る",
        href: "/blog/hokuriku-gym-guide-2026",
      },
    ],
    seo: {
      title: "石川県の体操教室6選【2026年最新】金沢・小松のおすすめまとめ",
      description:
        "石川県の体操教室を徹底比較。金沢市(ココナスポーツクラブ・畝田・工大前・駅西・城北)から小松市まで、教室ごとの特徴とクラスをまとめて紹介します。",
      keywords: [
        "石川県 体操教室",
        "金沢市 体操教室",
        "小松市 体操教室",
        "石川県 体操教室 おすすめ",
        "石川県 体操教室 比較",
      ],
    },
  },
  {
    id: "toyama-gym-guide-2026",
    title: "富山県の体操教室3選【2026年最新】堀川・高岡・砺波のおすすめまとめ",
    date: "2026年7月21日",
    excerpt:
      "富山県で体操教室を探すなら必見。富山市の堀川体操教室をはじめ、高岡市・砺波市の教室まで、教室ごとの特徴とクラスをまとめました。",
    image: Matto3,
    content: [
      {
        type: "paragraph",
        text: "富山県には富山市・高岡市・砺波市にそれぞれ体操教室があり、教室ごとにクラス編成が異なります。この記事では富山県内の体操教室を教室ごとに、特徴・対象年齢まで詳しくご紹介します。",
      },

      { type: "heading", text: "富山市堀川体操教室【富山市】" },
      {
        type: "paragraph",
        text: "富山市堀川町のマイプラザ2F(ダイソー前)にある教室です。体幹・体づくりクラスから選手大会を目指すクラスまで、目的とレベルに合わせて選べる7つのクラスを用意しているのが特徴です。",
      },
      {
        type: "table",
        caption: "クラス一覧",
        headers: ["クラス"],
        rows: [
          ["体幹・体づくり"],
          ["ジュニア初級"],
          ["ジュニア"],
          ["キッズ＋バク転"],
          ["クッション＋バク転"],
          ["アスリート"],
          ["選手・大会"],
        ],
      },
      { type: "cta", text: "富山市堀川体操教室の教室ページを見る", href: "/horikawa" },

      { type: "heading", text: "富山県高岡体操教室【高岡市】" },
      {
        type: "paragraph",
        text: "富山県高岡市中川栄町にある教室です。幼児①・幼児②の2段階で無理なく基礎を身につけたあと、キッズ・ジュニア、クッション・バク転へとステップアップしていけるクラス編成が特徴です。",
      },
      {
        type: "table",
        caption: "クラス一覧",
        headers: ["クラス", "対象"],
        rows: [
          ["幼児①", "幼児（3歳〜5歳）"],
          ["幼児②", "幼児（3歳〜5歳）"],
          ["キッズ", "5歳〜小学2年"],
          ["ジュニア", "小学生"],
          ["クッション・バク転", "小学生〜"],
        ],
      },
      { type: "cta", text: "富山県高岡体操教室の教室ページを見る", href: "/takaoka" },

      { type: "heading", text: "富山県砺波体操教室【砺波市】" },
      {
        type: "paragraph",
        text: "富山県砺波市大辻にある教室です。高岡体操教室と同じクラス編成で運営されており、幼児から小学生まで段階的に通えます。",
      },
      {
        type: "table",
        caption: "クラス一覧",
        headers: ["クラス", "対象"],
        rows: [
          ["幼児①", "幼児（3歳〜5歳）"],
          ["幼児②", "幼児（3歳〜5歳）"],
          ["キッズ", "5歳〜小学2年"],
          ["ジュニア", "小学生"],
          ["クッション・バク転", "小学生〜"],
        ],
      },
      { type: "cta", text: "富山県砺波体操教室の教室ページを見る", href: "/tonami" },

      { type: "heading", text: "富山県の体操教室選びでよくある質問" },
      {
        type: "faq",
        items: [
          {
            q: "富山県の体操教室は何歳から通えますか?",
            a: "教室によって異なりますが、幼児（3歳〜5歳）向けのクラスから受け付けている教室が多くあります。年齢や発達に不安がある場合は、まず無料体験でお子さまの様子を見てもらうのがおすすめです。",
          },
          {
            q: "富山市・高岡市・砺波市の教室で違いはありますか?",
            a: "富山市堀川体操教室は体幹・体づくりから選手大会クラスまで幅広いレベルに対応しているのに対し、高岡・砺波の教室は幼児から段階的にステップアップできるクラス編成が特徴です。目的やお子さまのレベルに合わせて選ぶとよいでしょう。",
          },
          {
            q: "バク転はどのクラスから挑戦できますか?",
            a: "多くの教室で「クッション・バク転」クラスが用意されており、小学生から挑戦できます。まずは基礎クラスで体づくりをしてからステップアップするのが一般的です。",
          },
        ],
      },
      {
        type: "paragraph",
        text: "同じ富山県内でも教室によってクラス構成が異なりますので、お子さまの年齢・レベルに合わせて教室を選び、まずは無料体験レッスンからお試しください。",
      },
      {
        type: "link",
        text: "北陸(石川・富山・福井)の体操教室まとめ記事も見る",
        href: "/blog/hokuriku-gym-guide-2026",
      },
    ],
    seo: {
      title: "富山県の体操教室3選【2026年最新】堀川・高岡・砺波のおすすめまとめ",
      description:
        "富山県の体操教室を徹底比較。富山市堀川体操教室、富山県高岡体操教室、富山県砺波体操教室の特徴とクラスをまとめて紹介します。",
      keywords: [
        "富山県 体操教室",
        "富山市 体操教室",
        "高岡市 体操教室",
        "砺波市 体操教室",
        "富山県 体操教室 おすすめ",
      ],
    },
  },
  {
    id: "fukui-gym-guide-2026",
    title: "福井県の体操教室｜福井市・小浜市｜幼児・小学生の無料体験受付中",
    date: "2026年7月21日",
    excerpt:
      "福井県福井市・小浜市の体操教室を紹介。幼児・小学生向けクラスがあり、無料体験も受付中です。教室ごとの特徴をわかりやすくまとめました。",
    image: Tetubou2,
    content: [
      {
        type: "paragraph",
        text: "福井県福井市・小浜市には、幼児(3歳〜5歳)から小学生まで通える体操教室があります。この記事では福井県内の体操教室を教室ごとに、特徴・対象年齢・クラスまで詳しくご紹介します。いずれの教室も無料体験を受け付けています。",
      },
      { type: "heading", text: "福井体操教室【福井市】" },
      {
        type: "paragraph",
        text: "福井県福井市二の宮のジョイライフ内で開催している教室です。幼児から小学生まで通いやすいクラス編成になっています。",
      },
      {
        type: "table",
        caption: "クラス一覧",
        headers: ["クラス", "対象"],
        rows: [
          ["幼児", "幼児（3歳〜5歳）"],
          ["キッズ", "5歳〜小学2年"],
          ["ジュニア", "小学生"],
          ["クッション・バク転", "小学生〜"],
        ],
      },
      { type: "cta", text: "福井体操教室の教室ページを見る", href: "/fukui" },

      { type: "heading", text: "パームスポーツクラブ【小浜市】" },
      {
        type: "paragraph",
        text: "福井県小浜市後瀬町、小浜市民体育館で開催している教室です。「子どものやる気と自信を育てる」をコンセプトに、体操を主軸としながら遊び感覚で自然と体をコントロールする力を養います。夏合宿や参観日、発表会などのイベントのほか、地域の高齢者施設訪問や保育園での体操指導といった地域貢献活動にも取り組んでいるのが特徴です。",
      },
      {
        type: "table",
        caption: "クラス一覧",
        headers: ["クラス"],
        rows: [
          ["キッズ体操"],
          ["ジュニア体操"],
          ["クッション体操"],
          ["知育スポーツクラス"],
        ],
      },
      {
        type: "note",
        text: "オプション教室:プログラミング教室／バルシューレ教室／バク転・アクロバット／忍者教室／チアリーディング教室",
      },
      { type: "cta", text: "パームスポーツクラブの公式サイトを見る", href: "https://cocona-sfc.jimdofree.com/", external: true },

      { type: "heading", text: "福井県の体操教室選びでよくある質問" },
      {
        type: "faq",
        items: [
          {
            q: "福井県の体操教室は何歳から通えますか?",
            a: "教室によって異なりますが、幼児（3歳〜5歳）向けのクラスから受け付けている教室が多くあります。まずは無料体験でお子さまの様子を見てもらうのがおすすめです。",
          },
          {
            q: "福井市と小浜市で教室の雰囲気に違いはありますか?",
            a: "福井体操教室はどのクラスも同じ料金体系で通いやすいのが特徴です。パームスポーツクラブは体操を主軸にプログラミングやチアリーディングなどのオプション教室も充実しているのが特徴です。",
          },
          {
            q: "運動が苦手な子でも通えますか?",
            a: "はい。どちらの教室も「できた」を積み重ねながら基礎から指導しているため、運動が苦手なお子さまでも安心して通えます。まずは無料体験レッスンにお申し込みください。",
          },
        ],
      },
      {
        type: "paragraph",
        text: "福井県内で体操教室をお探しの方は、まずはお近くの教室の無料体験レッスンにお申し込みください。",
      },
      {
        type: "link",
        text: "北陸(石川・富山・福井)の体操教室まとめ記事も見る",
        href: "/blog/hokuriku-gym-guide-2026",
      },
    ],
    seo: {
      title: "福井県の体操教室｜福井市・小浜市｜幼児・小学生の無料体験受付中",
      description:
        "福井県福井市・小浜市の体操教室を紹介。幼児・小学生向けクラスがあり、無料体験も受付中です。教室ごとの特徴をわかりやすくまとめました。",
      keywords: [
        "福井県 体操教室",
        "福井市 体操教室",
        "小浜市 体操教室",
        "福井 体操教室 幼児",
        "福井 体操教室 小学生",
        "福井県福井市 体操教室",
        "福井 体操教室 無料体験",
      ],
    },
  },
  {
    id: "hokuriku-gym-guide-2026",
    title: "北陸の体操教室完全ガイド【2026年最新】石川・富山・福井を徹底比較",
    date: "2026年7月21日",
    excerpt:
      "北陸で体操教室を探すなら必見。石川・富山・福井、北陸3県の体操教室をエリア別にまとめて比較。お住まいの地域に合わせて、通いやすい教室を見つけましょう。",
    image: Tobibako1,
    content: [
      {
        type: "paragraph",
        text: "北陸エリア(石川県・富山県・福井県)には、それぞれの地域に体操教室があります。この記事では北陸の体操教室を地域ごとに横断的に紹介し、詳細は地域別のまとめ記事へのリンクからご確認いただけます。",
      },
      {
        type: "table",
        caption: "北陸エリアの体操教室 一覧",
        headers: ["都道府県", "教室"],
        rows: [
          ["石川県", "ココナスポーツクラブ・畝田体操教室・工大前教室・駅西教室(金沢市)"],
          ["石川県", "金沢市城北体操教室(城北・石川工大前)"],
          ["石川県", "小松体操教室(小松市)"],
          ["富山県", "富山市堀川体操教室"],
          ["富山県", "富山県高岡体操教室・富山県砺波体操教室"],
          ["福井県", "福井体操教室(福井市)"],
          ["福井県", "パームスポーツクラブ(小浜市)"],
        ],
      },
      { type: "heading", text: "エリア別の詳しいまとめ記事" },
      {
        type: "link",
        text: "石川県のおすすめ体操教室まとめを見る",
        href: "/blog/ishikawa-gym-guide-2026",
      },
      {
        type: "link",
        text: "富山県のおすすめ体操教室まとめを見る",
        href: "/blog/toyama-gym-guide-2026",
      },
      {
        type: "link",
        text: "福井県のおすすめ体操教室まとめを見る",
        href: "/blog/fukui-gym-guide-2026",
      },
      { type: "heading", text: "北陸の体操教室選びでよくある質問" },
      {
        type: "faq",
        items: [
          {
            q: "北陸(石川・富山・福井)で体操教室はどこにありますか?",
            a: "石川県は金沢市(ココナスポーツクラブ・畝田・工大前・駅西・城北)と小松市、富山県は富山市・高岡市・砺波市、福井県は福井市・小浜市に教室があります。詳しくは各都道府県のまとめ記事をご覧ください。",
          },
          {
            q: "体操教室はどうやって選べばよいですか?",
            a: "自宅や学校からの通いやすさ、クラスの曜日・時間、少人数制かフリー制かといった指導スタイルを比較するのがおすすめです。まずは気になる教室の無料体験に参加してみてください。",
          },
          {
            q: "無料体験は誰でも申し込めますか?",
            a: "はい。ほとんどの教室で年齢を問わず無料体験を受け付けています。各教室ページの「教室ページを見る」ボタンから詳細をご確認ください。",
          },
        ],
      },
      {
        type: "paragraph",
        text: "最新のクラス内容は各教室のページ、またはお問い合わせフォームよりご確認ください。",
      },
    ],
    seo: {
      title: "北陸の体操教室完全ガイド【2026年最新】石川・富山・福井を徹底比較",
      description:
        "北陸(石川・富山・福井)の体操教室を徹底比較できる完全ガイド。地域ごとの教室とクラス内容をまとめて紹介します。",
      keywords: [
        "北陸 体操教室",
        "石川県 体操教室",
        "富山県 体操教室",
        "福井県 体操教室",
        "北陸 体操教室 比較",
      ],
    },
  },
  {
    id: "horikawa-agarikomi-tips-2026",
    title: "逆上がりのコツ3つ｜富山市堀川の土日だけ体操教室",
    date: "2026年9月3日",
    excerpt:
      "逆上がりができずに悩んでいませんか？富山市堀川の土日だけ体操教室 堀川店が、鉄棒の逆上がりが得意になる練習のポイントを3つに分けて紹介します。",
    image: Tetubou1,
    imageAlt: "富山市堀川の教室で鉄棒の逆上がりに挑戦する子ども",
    storeId: "horikawa",
    content: [
      {
        type: "paragraph",
        text: "「逆上がりができなくて自信をなくしている」「学校の体育の時間が近づくと憂うつそう」——富山市堀川の土日だけ体操教室 堀川店には、そんなお悩みを抱えたお子さまが数多く体験にいらっしゃいます。逆上がりは筋力だけでなく、体の使い方のコツを知っているかどうかで大きく変わる技です。今回は、堀川店のレッスンで実際に指導しているポイントを3つに絞ってご紹介します。",
      },
      { type: "heading", text: "逆上がりでよくある悩み" },
      {
        type: "paragraph",
        text: "堀川店に通うお子さまの多くが最初につまずくのは「お腹が鉄棒から離れてしまう」「脚を振り上げる勢いが足りない」という2点です。腕の力だけで体を持ち上げようとしてしまい、途中で諦めてしまうケースがよく見られます。実はこれは筋力不足というより、体を鉄棒に近づける感覚や、脚を振り上げるタイミングがつかめていないことが原因であるケースがほとんどです。",
      },
      { type: "heading", text: "練習のポイント3つ" },
      {
        type: "table",
        caption: "逆上がり上達の3ポイント",
        headers: ["ポイント", "内容"],
        rows: [
          ["① おへそを鉄棒に近づける", "腕を曲げて体を鉄棒にしっかり引きつける感覚をつかむ"],
          ["② 脚を強く振り上げる", "後ろに振ってから前に大きく振り上げる勢いをつける"],
          ["③ 回転中も鉄棒を見る", "顔を下げず鉄棒方向を見続けることで体が丸まりやすくなる"],
        ],
      },
      {
        type: "paragraph",
        text: "この3つは学校の体育でも指導されますが、実際に体を使って何度も練習しないと感覚として身につきません。富山市堀川の土日だけ体操教室では、補助具や先生のサポートを使いながら、この3つの感覚を1つずつ丁寧に練習していきます。",
      },
      { type: "heading", text: "堀川店でのサポート内容" },
      {
        type: "paragraph",
        text: "堀川店の「クッション・バク転」クラスや「ジュニア」クラスでは、逆上がりが苦手なお子さまに対して、補助板やベルトを使った段階練習を取り入れています。いきなり鉄棒だけで挑戦させるのではなく、マットやとび箱を使った準備運動で体幹を鍛えてから鉄棒に取り組むことで、無理なく「できた！」を積み重ねられるようにしています。少人数制なので、お子さま一人ひとりのつまずきポイントに合わせた声かけができるのも特徴です。",
      },
      {
        type: "note",
        text: "堀川店は富山県富山市堀川町355-7 マイプラザ2F（ダイソー前）。土/日開催で、幼児から選手大会クラスまで7つのクラスがあります。",
      },
      {
        type: "paragraph",
        text: "逆上がりは「できた」瞬間にお子さまの自信が大きく育つ技のひとつです。おうちの鉄棒がなくても、教室で正しいコツを繰り返し練習すれば必ず上達します。富山市堀川で逆上がりの練習をお探しなら、まずは無料体験レッスンで教室の雰囲気を体感してみてください。",
      },
      {
        type: "cta",
        text: "堀川店の無料体験に申し込む",
        href: "https://free-trial.tht-group.co.jp/",
        external: true,
      },
      { type: "cta", text: "富山市堀川体操教室の教室ページを見る", href: "/horikawa" },
      {
        type: "link",
        text: "富山県の体操教室まとめ記事も見る",
        href: "/blog/toyama-gym-guide-2026",
      },
    ],
    seo: {
      title: "逆上がりのコツ3つ｜富山市堀川の土日だけ体操教室",
      description:
        "逆上がりが苦手なお子さま向けに、富山市堀川の土日だけ体操教室 堀川店が鉄棒の逆上がり上達のコツを3つ紹介。無料体験レッスン受付中です。",
      keywords: [
        "堀川 逆上がり",
        "逆上がり コツ",
        "富山市堀川 体操教室",
        "鉄棒 練習",
        "堀川店",
        "小学生 体操教室",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "takaoka-happyoukai-2026-09",
    title: "発表会レポート｜富山県高岡市の土日だけ体操教室",
    date: "2026年9月3日",
    excerpt:
      "富山県高岡市の土日だけ体操教室 高岡店で行われた発表会の様子をレポート。クッション・バク転クラスの子どもたちの挑戦と成長をお届けします。",
    image: Tobibako3,
    imageAlt: "富山県高岡市の教室の発表会で跳び箱の技を披露する子どもたち",
    storeId: "takaoka",
    content: [
      {
        type: "paragraph",
        text: "先日、富山県高岡市の土日だけ体操教室 高岡店で、日頃の練習の成果を発表する「教室内発表会」を行いました。今回はキッズクラスからクッション・バク転クラスまで、それぞれのレベルに合わせた技を保護者の皆さまの前で披露してもらいました。",
      },
      { type: "heading", text: "発表会当日の様子" },
      {
        type: "paragraph",
        text: "幼児①・幼児②クラスの小さな子どもたちは、マット運動での前転や、平均台を使ったバランス運動に挑戦。緊張した面持ちでスタートラインに立つ姿は、いつものレッスンとは少し違う真剣な表情でした。キッズ・ジュニアクラスは跳び箱と鉄棒の技を組み合わせた演技を披露し、クッション・バク転クラスの子どもたちは、練習を重ねてきたバク転にも果敢に挑戦してくれました。",
      },
      { type: "heading", text: "子どもたちの反応" },
      {
        type: "paragraph",
        text: "発表を終えた子どもたちからは「緊張したけど成功して嬉しかった」「次はもっと難しい技に挑戦したい」といった声が聞かれました。中には本番でバク転が決まり、思わずガッツポーズをする姿も。保護者の皆さまからも「普段の練習の成果がよくわかった」「子どもの成長を実感できた」と嬉しいお言葉をいただきました。高岡市の土日だけ体操教室では、こうした発表の場を通じて、子どもたちが「できた」を実感し、次への挑戦意欲につなげられるよう取り組んでいます。",
      },
      {
        type: "note",
        text: "高岡店は富山県高岡市中川栄町6-10。幼児①・幼児②からクッション・バク転クラスまで、無理なくステップアップできるクラス編成です。",
      },
      {
        type: "paragraph",
        text: "次回の発表会は冬頃を予定しています。高岡体操教室では引き続き、一人ひとりのペースに合わせた指導で、子どもたちの「やってみたい」を応援していきます。",
      },
      {
        type: "cta",
        text: "高岡店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=takaoka",
        external: true,
      },
      { type: "cta", text: "富山県高岡体操教室の教室ページを見る", href: "/takaoka" },
      {
        type: "link",
        text: "富山県の体操教室まとめ記事も見る",
        href: "/blog/toyama-gym-guide-2026",
      },
    ],
    seo: {
      title: "発表会レポート｜富山県高岡市の土日だけ体操教室",
      description:
        "富山県高岡市の土日だけ体操教室 高岡店の発表会レポート。子どもたちの挑戦と成長の様子をお届けします。無料体験レッスン受付中。",
      keywords: [
        "高岡 体操教室 発表会",
        "高岡市 体操教室",
        "高岡店",
        "富山県 体操教室",
        "幼児 体操教室",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "tonami-nigate-column-2026",
    title: "運動が苦手な子への向き合い方｜砺波市の体操教室",
    date: "2026年9月3日",
    excerpt:
      "運動が苦手なお子さまへの向き合い方を、富山県砺波市の土日だけ体操教室 砺波店の指導経験からご紹介。無理なく自信を育てる考え方をまとめました。",
    image: Matto4,
    imageAlt: "富山県砺波市の教室でマット運動の練習をする子ども",
    storeId: "tonami",
    content: [
      {
        type: "paragraph",
        text: "「うちの子は運動が苦手だから体操教室についていけるか心配」——富山県砺波市の土日だけ体操教室 砺波店には、こうしたご相談をよくいただきます。運動が得意な子だけが楽しめる場所ではなく、苦手なお子さまこそ小さな成功体験を積み重ねられる場でありたいと私たちは考えています。",
      },
      { type: "heading", text: "考え方:「できた」を細かく分ける" },
      {
        type: "paragraph",
        text: "運動が苦手なお子さまの多くは、周りと比べて「自分はできない」と感じてしまい、挑戦する前に諦めてしまいがちです。大切なのは、技全体を一度に完成させようとせず、できるようになるまでの過程を細かいステップに分けることです。例えば跳び箱なら「踏み切り板を強く踏む」「手を遠くにつく」「お尻を高く上げる」というように、1つずつのステップで「できた」を実感できるようにすることで、少しずつ自信が積み上がっていきます。",
      },
      { type: "heading", text: "砺波店での実践" },
      {
        type: "paragraph",
        text: "砺波体操教室では、幼児①・幼児②クラスから少人数制でレッスンを行い、一人ひとりの得意・不得意に合わせて声かけの仕方や練習メニューを調整しています。周りのお子さまと比較するのではなく、その子自身の「前回できなかったことが今日できた」という変化に注目して褒めることを大切にしています。また、キッズ・ジュニアクラスでは、同じ技でも複数の難易度の練習方法を用意し、無理のない挑戦から始められるようにしています。",
      },
      {
        type: "paragraph",
        text: "運動が苦手というのは、決して「向いていない」ということではありません。正しいステップと、安心して挑戦できる環境があれば、どのお子さまも確実に成長していきます。砺波市で体操教室をお探しなら、まずは無料体験でお子さまの様子をご覧ください。",
      },
      {
        type: "cta",
        text: "砺波店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=tonami",
        external: true,
      },
      { type: "cta", text: "富山県砺波体操教室の教室ページを見る", href: "/tonami" },
      {
        type: "link",
        text: "富山県の体操教室まとめ記事も見る",
        href: "/blog/toyama-gym-guide-2026",
      },
    ],
    seo: {
      title: "運動が苦手な子への向き合い方｜砺波市の体操教室",
      description:
        "運動が苦手なお子さまへの向き合い方を、富山県砺波市の土日だけ体操教室 砺波店の指導経験からご紹介。無料体験レッスン受付中です。",
      keywords: [
        "砺波 運動 苦手 克服",
        "砺波市 体操教室",
        "砺波店",
        "富山県 体操教室",
        "子ども 運動 苦手",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "komatsu-tobibako-tips-2026",
    title: "跳び箱の練習のコツ｜石川県小松市の体操教室",
    date: "2026年9月3日",
    excerpt:
      "跳び箱が苦手な子必見。石川県小松市の小松体操教室(小松店)が、跳び箱を跳べるようになる3つの練習のポイントを紹介します。無料体験受付中。",
    image: Tobibako4,
    imageAlt: "石川県小松市の教室で跳び箱に挑戦する子ども",
    storeId: "komatsu",
    content: [
      {
        type: "paragraph",
        text: "「跳び箱の前で止まってしまう」「怖くて踏み切れない」——石川県小松市の小松体操教室(小松店)にも、跳び箱が苦手なお子さまが多く通っています。跳び箱は恐怖心が大きな壁になりやすい種目ですが、コツをつかめば段階的に必ず跳べるようになります。今回は小松体操教室での指導のポイントを紹介します。",
      },
      { type: "heading", text: "跳び箱でよくある悩み" },
      {
        type: "paragraph",
        text: "小松体操教室のキッズクラスでは「助走のスピードが足りず踏み切れない」「手を跳び箱の手前につきすぎてしまう」というつまずきがよく見られます。特に跳び箱の高さに対する恐怖心があると、踏み切りの直前でブレーキがかかってしまい、勢いが失われてうまく跳べないというケースが多くあります。",
      },
      { type: "heading", text: "練習のポイント3つ" },
      {
        type: "table",
        caption: "跳び箱上達の3ポイント",
        headers: ["ポイント", "内容"],
        rows: [
          ["① 助走で加速する", "跳び箱に近づくほどスピードを上げて踏み切りの勢いをつける"],
          ["② 踏み切り板を強く踏む", "両足で強く踏み切ることで体を高く浮かせる"],
          ["③ 手を遠くにつく", "跳び箱の奥に手をつくことでお尻を高く上げやすくなる"],
        ],
      },
      {
        type: "paragraph",
        text: "低い段数から始めて、この3つのポイントを1つずつ確認しながら練習することで、恐怖心を減らしながら着実にステップアップできます。",
      },
      { type: "heading", text: "小松体操教室でのサポート内容" },
      {
        type: "paragraph",
        text: "小松体操教室のキッズバク転クラスやクッション・バク転クラスでは、跳び箱が苦手なお子さまに対して、段数を細かく調整しながら「できた」を積み重ねられるよう指導しています。踏み切り板の位置や手のつき方を一人ひとり確認しながら、無理に高い段数へ挑戦させるのではなく、確実に跳べる高さから少しずつレベルアップしていくのが小松体操教室のスタイルです。",
      },
      {
        type: "note",
        text: "小松体操教室(小松店)は石川県小松市光町47。土/日開催で、幼児5歳から小学生まで通えるクラスがあります。",
      },
      {
        type: "cta",
        text: "小松店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=komatsu",
        external: true,
      },
      { type: "cta", text: "小松体操教室の教室ページを見る", href: "/komatsu" },
      {
        type: "link",
        text: "石川県の体操教室まとめ記事も見る",
        href: "/blog/ishikawa-gym-guide-2026",
      },
    ],
    seo: {
      title: "跳び箱の練習のコツ｜石川県小松市の体操教室",
      description:
        "跳び箱が苦手なお子さま向けに、石川県小松市の小松体操教室(小松店)が跳び箱上達の3つのコツを紹介。無料体験レッスン受付中です。",
      keywords: [
        "小松 跳び箱 コツ",
        "跳び箱 練習方法",
        "小松体操教室",
        "小松市 体操教室",
        "小学生 体操教室",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "ishikawa-taiken-event-2026",
    title: "無料体験会レポート｜石川県金沢市鳴和の体操教室",
    date: "2026年9月3日",
    excerpt:
      "石川県金沢市鳴和の土日だけ体操教室 鳴和店で開催した無料体験会の様子をレポート。初めて体操に挑戦した子どもたちの表情をお届けします。",
    image: Tobibako5,
    imageAlt: "石川県金沢市鳴和の教室の体験会でとび箱に挑戦する子ども",
    storeId: "ishikawa",
    content: [
      {
        type: "paragraph",
        text: "石川県金沢市鳴和の土日だけ体操教室 鳴和店では、先日、初めて体操に触れるお子さま向けの無料体験会を開催しました。マット運動・とび箱・鉄棒・トランポリンの4種目を一通り体験してもらい、体操の楽しさを知ってもらう機会になりました。",
      },
      { type: "heading", text: "体験会の様子" },
      {
        type: "paragraph",
        text: "当日は年少から小学生まで、幅広い年齢のお子さまにご参加いただきました。初めての鉄棒に戸惑いながらも、先生のサポートを受けながら少しずつ体を持ち上げるコツをつかんでいく姿や、トランポリンの上で楽しそうに跳びはねる笑顔が印象的でした。中には「跳び箱を跳ぶのが怖い」と言っていたお子さまが、低い段数から挑戦して見事に跳べた瞬間、周りから拍手が起こる場面もありました。",
      },
      { type: "heading", text: "子どもたちの反応" },
      {
        type: "paragraph",
        text: "体験会後、参加してくれたお子さまからは「またやりたい」「鉄棒が楽しかった」という声を多くいただきました。保護者の方からも「先生が優しく声をかけてくれて安心して見ていられた」「運動が苦手な子でも楽しめていた」といった感想をいただき、金沢市鳴和エリアで安心して通える体操教室として、改めて手応えを感じる機会になりました。",
      },
      {
        type: "note",
        text: "鳴和店は石川県金沢市鳴和2丁目10-44。年少〜年中の基礎クラスから、中学生・高校生・大人向けの本格的な体幹トレーニングまで幅広く対応しています。",
      },
      {
        type: "paragraph",
        text: "次回の体験会も随時受け付けています。金沢市鳴和で体操教室をお探しの方は、まずはお気軽にご参加ください。",
      },
      {
        type: "cta",
        text: "鳴和店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=ishikawa",
        external: true,
      },
      { type: "cta", text: "金沢市城北体操教室の教室ページを見る", href: "/ishikawa" },
      {
        type: "link",
        text: "石川県の体操教室まとめ記事も見る",
        href: "/blog/ishikawa-gym-guide-2026",
      },
    ],
    seo: {
      title: "無料体験会レポート｜石川県金沢市鳴和の体操教室",
      description:
        "石川県金沢市鳴和の土日だけ体操教室 鳴和店の無料体験会レポート。初めて体操に挑戦した子どもたちの様子をお届けします。",
      keywords: [
        "鳴和 体操教室 体験会",
        "金沢市鳴和 体操教室",
        "鳴和店",
        "石川県 体操教室",
        "幼児 体操教室",
        "体操教室 無料体験会",
      ],
    },
  },
  {
    id: "fukui-zenkai-koukai-tips-2026",
    title: "マット運動 前転・後転のコツ｜福井市の体操教室",
    date: "2026年9月3日",
    excerpt:
      "前転・後転がうまくできないお子さま向けに、福井県福井市の土日だけ体操教室 福井店がマット運動のコツを紹介。無料体験レッスン受付中です。",
    image: Matto5,
    imageAlt: "福井県福井市の教室でマット運動の前転に挑戦する子ども",
    storeId: "fukui",
    content: [
      {
        type: "paragraph",
        text: "「前転で横に転がってしまう」「後転で首が痛くなる」——福井県福井市の土日だけ体操教室 福井店には、マット運動の基本である前転・後転に苦戦するお子さまがよく体験にいらっしゃいます。前転・後転はすべての体操種目の基礎になる動きなので、正しいコツを早い段階で身につけることが大切です。",
      },
      { type: "heading", text: "よくある悩み" },
      {
        type: "paragraph",
        text: "福井体操教室の幼児クラス・キッズクラスでは「あごを引かずに回ってしまい首に負担がかかる」「手のつく位置が近すぎてまっすぐ回転できない」というつまずきがよく見られます。恐怖心から体が固まってしまい、勢いよく回転できないお子さまも少なくありません。",
      },
      { type: "heading", text: "練習のポイント3つ" },
      {
        type: "table",
        caption: "前転・後転上達の3ポイント",
        headers: ["ポイント", "内容"],
        rows: [
          ["① おへそを見る", "あごを引いておへそを見ることで、頭を守りながら丸く回転できる"],
          ["② 手を肩幅より少し前につく", "手をつく位置を体から離すことでスムーズに回転できる"],
          ["③ 勢いをつけて回る", "怖がってゆっくり回ろうとせず、思い切って回転することで安定する"],
        ],
      },
      { type: "heading", text: "福井店でのサポート内容" },
      {
        type: "paragraph",
        text: "福井体操教室では、幼児クラスから丁寧に体の使い方を指導し、坂道マットや補助を使いながら回転の感覚を段階的に身につけられるようにしています。恐怖心が強いお子さまには無理に回転させず、まずは前後にゆれる感覚をつかむ練習から始めるなど、一人ひとりのペースに合わせた指導を大切にしています。",
      },
      {
        type: "note",
        text: "福井店は福井県福井市二の宮3丁目42-12 ジョイライフ。幼児(3歳)から通えるクラスがあり、無料体験を受け付けています。",
      },
      {
        type: "cta",
        text: "福井店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=fukui",
        external: true,
      },
      { type: "cta", text: "福井体操教室の教室ページを見る", href: "/fukui" },
      {
        type: "link",
        text: "福井県の体操教室まとめ記事も見る",
        href: "/blog/fukui-gym-guide-2026",
      },
    ],
    seo: {
      title: "マット運動 前転・後転のコツ｜福井市の体操教室",
      description:
        "前転・後転が苦手なお子さま向けに、福井県福井市の土日だけ体操教室 福井店がマット運動上達のコツを紹介。無料体験レッスン受付中です。",
      keywords: [
        "福井 前転 後転 コツ",
        "前転 コツ",
        "後転 コツ",
        "福井市 体操教室",
        "福井店",
        "幼児 体操教室",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "mutsukawa-yaruki-column-2026",
    title: "子どものやる気を育てる習い事の続け方｜六ツ川店",
    date: "2026年9月3日",
    excerpt:
      "習い事が続かないと悩む保護者の方へ。横浜市南区六ツ川の土日だけ体操教室 南区六ツ川店が、子どものやる気を育てる関わり方を紹介します。",
    image: Matto6,
    imageAlt: "神奈川県横浜市南区六ツ川の教室でマット運動に取り組む子ども",
    storeId: "mutsukawa",
    content: [
      {
        type: "paragraph",
        text: "「習い事を始めてもすぐに『やめたい』と言い出す」「やる気が続かず親としてどう声をかければいいかわからない」——神奈川県横浜市南区六ツ川の土日だけ体操教室 南区六ツ川店にも、こうしたご相談を寄せる保護者の方が少なくありません。今回は、子どものやる気を育てるための考え方についてご紹介します。",
      },
      { type: "heading", text: "考え方:結果より過程を認める" },
      {
        type: "paragraph",
        text: "子どものやる気は「できた・できなかった」という結果だけで判断されると、失敗を恐れて挑戦自体を避けるようになってしまうことがあります。大切なのは、技ができたかどうかだけでなく、「難しい技に挑戦した」「前回より高く跳べた」といった過程の変化を見つけて言葉にして伝えることです。過程を認められた経験が積み重なることで、子どもは「やってみよう」という気持ちを持ちやすくなります。",
      },
      { type: "heading", text: "六ツ川店での実践" },
      {
        type: "paragraph",
        text: "南区六ツ川店では、リトル・キッズクラスからジュニア、クッションクラスまで、少人数制のレッスンで先生が一人ひとりの取り組みを丁寧に見守っています。技の完成度だけでなく、練習に向き合う姿勢や小さな進歩を積極的に声に出して褒めることで、子どもが「次も頑張りたい」と思えるような関わり方を大切にしています。また体操体幹クラスでは基礎体力づくりを通じて、無理なく自信を積み重ねられるようにしています。",
      },
      {
        type: "paragraph",
        text: "習い事が長く続くかどうかは、子ども自身が「やらされている」のではなく「自分で挑戦している」と感じられるかどうかが大きなポイントです。横浜市南区六ツ川で体操教室をお探しの方は、まずは無料体験でお子さまの反応を見てみてください。",
      },
      {
        type: "cta",
        text: "六ツ川店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=yokohama",
        external: true,
      },
      { type: "cta", text: "南区六ツ川体操教室の教室ページを見る", href: "/mutsukawa" },
      {
        type: "link",
        text: "北陸(石川・富山・福井)の体操教室まとめ記事も見る",
        href: "/blog/hokuriku-gym-guide-2026",
      },
    ],
    seo: {
      title: "子どものやる気を育てる習い事の続け方｜六ツ川店",
      description:
        "習い事が続かないとお悩みの方へ。横浜市南区六ツ川の土日だけ体操教室 南区六ツ川店が、子どものやる気を育てる関わり方を紹介します。",
      keywords: [
        "六ツ川 習い事 続かない",
        "子ども やる気",
        "横浜市南区 体操教室",
        "六ツ川店",
        "体操教室 選び方",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "horikawa-honban-tsuyoi-column-2026",
    title: "本番に強い子を育てる声かけ｜富山市堀川の体操教室",
    date: "2026年9月7日",
    excerpt:
      "発表会や大会前に緊張してしまうお子さまへの声かけ方法を、富山市堀川の土日だけ体操教室 堀川店の指導経験からご紹介。本番に強い子を育てるヒントをお届けします。",
    image: Core1,
    imageAlt: "富山市堀川の教室で発表会に向けて練習に取り組む子ども",
    storeId: "horikawa",
    content: [
      {
        type: "paragraph",
        text: "発表会や大会が近づくと「緊張して普段の力が出せないのでは」と心配になる保護者の方は少なくありません。富山市堀川の土日だけ体操教室 堀川店には、幼児から選手大会クラスまで幅広い年齢のお子さまが通っており、日々の練習だけでなく「本番でどう力を発揮するか」にも向き合っています。今回は、本番に強い子を育てるための声かけの工夫についてご紹介します。",
      },
      { type: "heading", text: "考え方:結果ではなく準備の過程を認める" },
      {
        type: "paragraph",
        text: "本番に強い子を育てるうえで大切なのは、「成功するかどうか」だけに注目しないことです。結果ばかりを気にすると失敗への不安が大きくなり、かえって本来の力を発揮しにくくなってしまいます。堀川店では、技が「できたかどうか」よりも、そこに至るまでの練習の積み重ねや、挑戦しようとした姿勢そのものを認める声かけを大切にしています。「ここまで練習を頑張ってきたから大丈夫」という言葉が、お子さまの安心感につながります。",
      },
      { type: "heading", text: "堀川店での実践" },
      {
        type: "paragraph",
        text: "堀川店の選手大会クラスでは、大会が近づくと本番を想定した通し練習を取り入れ、緊張する場面に慣れることから始めます。いきなり大きな舞台を経験させるのではなく、小さな「人前でやってみる」機会を積み重ねることで、少しずつ本番への耐性を育てています。うまくいかなかった場合も「次はどうすればいいか」を一緒に考える時間を設け、失敗を次への学びに変えられるようサポートしています。ジュニアクラスやキッズクラスでも、発表の機会を通じて同じように「挑戦する経験」を積めるようにしています。",
      },
      {
        type: "paragraph",
        text: "本番への強さは、生まれつきの性格ではなく、日々の関わり方や経験の積み重ねで育てていけるものです。富山市堀川で発表会や大会にも挑戦できる体操教室をお探しなら、まずは無料体験で教室の雰囲気を感じてみてください。",
      },
      {
        type: "cta",
        text: "堀川店の無料体験に申し込む",
        href: "https://free-trial.tht-group.co.jp/",
        external: true,
      },
      { type: "cta", text: "富山市堀川体操教室の教室ページを見る", href: "/horikawa" },
      {
        type: "link",
        text: "富山県の体操教室まとめ記事も見る",
        href: "/blog/toyama-gym-guide-2026",
      },
    ],
    seo: {
      title: "本番に強い子を育てる声かけ｜富山市堀川の体操教室",
      description:
        "発表会や大会前に緊張してしまうお子さまへの声かけ方法を、富山市堀川の土日だけ体操教室 堀川店がご紹介。選手大会クラスもある堀川店で本番に強い子を育てるヒントをお届けします。無料体験受付中。",
      keywords: [
        "体操教室 本番に強い子",
        "発表会 声かけ",
        "堀川店",
        "富山市堀川 体操教室",
        "選手大会クラス",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "takaoka-itsukara-column-2026",
    title: "体操教室は何歳から?｜富山県高岡市の土日だけ体操教室",
    date: "2026年9月7日",
    excerpt:
      "体操教室は何歳から始めるべき?富山県高岡市の土日だけ体操教室 高岡店が、幼児期に体を動かす習い事を始めるメリットをご紹介します。",
    image: Matto2,
    imageAlt: "富山県高岡市の教室で幼児クラスの親子レッスンに取り組む様子",
    storeId: "takaoka",
    content: [
      {
        type: "paragraph",
        text: "「体操教室は何歳から始めればいいの?」というご質問を、富山県高岡市の土日だけ体操教室 高岡店でもよくいただきます。早すぎても続かないのでは、遅すぎても他の子に置いていかれるのでは、と迷う保護者の方は少なくありません。今回は、幼児期に体操教室を始めるメリットと、高岡店で実際に行っている無理のないスタートの仕方についてご紹介します。",
      },
      { type: "heading", text: "考え方:幼児期は「体の使い方」を覚える大切な時期" },
      {
        type: "paragraph",
        text: "幼児期(3歳〜5歳頃)は、走る・跳ぶ・転がるといった基本的な体の動かし方を吸収しやすい時期だといわれています。この時期にいろいろな動きを経験しておくことで、その後の運動能力の土台がつくられていきます。「運動が得意になってから始める」のではなく、「体の使い方を覚えるために早めに始める」という考え方が、幼児期の習い事選びでは大切です。",
      },
      { type: "heading", text: "高岡店での実践" },
      {
        type: "paragraph",
        text: "高岡体操教室では、幼児①・幼児②という2段階のクラスを用意し、保護者様と一緒にレッスンを行う幼児①から、少しずつ一人での参加に慣れていく幼児②へと、無理なくステップアップできる編成にしています。初めての集団活動でも、先生が一人ひとりのペースに合わせて声をかけるので、人見知りなお子さまでも安心して始められます。幼児クラスを卒業したお子さまは、キッズ・ジュニアクラスへとスムーズに進級していきます。",
      },
      {
        type: "paragraph",
        text: "体操教室を始めるタイミングに「早すぎる」はほとんどありません。高岡市で幼児期からの習い事をお探しなら、まずは無料体験でお子さまの様子をご覧ください。",
      },
      {
        type: "cta",
        text: "高岡店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=takaoka",
        external: true,
      },
      { type: "cta", text: "富山県高岡体操教室の教室ページを見る", href: "/takaoka" },
      {
        type: "link",
        text: "富山県の体操教室まとめ記事も見る",
        href: "/blog/toyama-gym-guide-2026",
      },
    ],
    seo: {
      title: "体操教室は何歳から?｜富山県高岡市の土日だけ体操教室",
      description:
        "体操教室は何歳から始めるべき?富山県高岡市の土日だけ体操教室 高岡店が、幼児期に体を動かす習い事を始めるメリットをご紹介。幼児①・幼児②の2段階クラスで無理なくスタートできます。",
      keywords: [
        "体操教室 何歳から",
        "幼児 体操教室",
        "高岡市 体操教室",
        "高岡店",
        "習い事 始めどき",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "tonami-jibunde-kangaeru-column-2026",
    title: "「言われたことしかしない」を卒業する｜砺波市の体操教室",
    date: "2026年9月7日",
    excerpt:
      "「言われたことしかできない」と感じるお子さまへの向き合い方を、富山県砺波市の土日だけ体操教室 砺波店の指導経験からご紹介。自分で考える力を育てる関わり方をまとめました。",
    image: Matto7,
    imageAlt: "富山県砺波市の教室で自分で考えながらマット運動に取り組む子ども",
    storeId: "tonami",
    content: [
      {
        type: "paragraph",
        text: "「指示されたことはできるけど、自分から考えて動くのが苦手」——富山県砺波市の土日だけ体操教室 砺波店にも、そんなお子さまの様子に不安を感じる保護者の方からのご相談があります。運動能力だけでなく、自分で考えて挑戦する力も、体操を通じて育てていくことができます。",
      },
      { type: "heading", text: "考え方:答えをすぐに教えない" },
      {
        type: "paragraph",
        text: "自分で考える力を育てるには、うまくいかない場面ですぐに答えを教えてしまわないことが大切です。「ここをこうしてごらん」とすべて指示してしまうと、お子さまは指示待ちの姿勢が身についてしまいます。まずは「どうすればうまくいくと思う?」と問いかけ、お子さま自身に考える時間を与えることで、失敗も含めて自分で試行錯誤する経験を積み重ねられます。",
      },
      { type: "heading", text: "砺波店での実践" },
      {
        type: "paragraph",
        text: "砺波体操教室のキッズ・ジュニアクラスでは、技のコツを一方的に教えるのではなく、「さっきと比べて何が変わった?」など、お子さま自身に気づきを促す声かけを心がけています。うまくいった時も、なぜうまくいったのかを一緒に振り返ることで、次に活かせる「自分で考える力」が育っていきます。幼児①・幼児②クラスの小さなお子さまにも、年齢に応じた簡単な問いかけから始めています。",
      },
      {
        type: "paragraph",
        text: "自分で考える力は、すぐには身につきませんが、日々の小さな積み重ねで確実に育っていきます。砺波市で体操教室をお探しの方は、まずは無料体験でお子さまの様子をご覧ください。",
      },
      {
        type: "cta",
        text: "砺波店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=tonami",
        external: true,
      },
      { type: "cta", text: "富山県砺波体操教室の教室ページを見る", href: "/tonami" },
      {
        type: "link",
        text: "富山県の体操教室まとめ記事も見る",
        href: "/blog/toyama-gym-guide-2026",
      },
    ],
    seo: {
      title: "「言われたことしかしない」を卒業する｜砺波市の体操教室",
      description:
        "「言われたことしかできない」と感じるお子さまへの向き合い方を、富山県砺波市の土日だけ体操教室 砺波店がご紹介。自分で考える力を育てる関わり方をまとめました。無料体験受付中。",
      keywords: [
        "体操教室 自分で考える力",
        "子ども 主体性",
        "砺波市 体操教室",
        "砺波店",
        "習い事 指導方針",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "komatsu-undoushinkei-column-2026",
    title: "運動神経は生まれつき?伸ばし方｜石川県小松市の体操教室",
    date: "2026年9月7日",
    excerpt:
      "「運動神経は生まれつき」と思っていませんか?石川県小松市の小松体操教室(小松店)が、バク転にも挑戦できる運動能力の伸ばし方をご紹介します。",
    image: Core2,
    imageAlt: "石川県小松市の教室でバク転の練習に挑戦する子ども",
    storeId: "komatsu",
    content: [
      {
        type: "paragraph",
        text: "「うちの子は運動神経が悪いから」と、体操教室に通わせることを迷う保護者の方もいらっしゃいます。しかし石川県小松市の小松体操教室(小松店)では、運動神経は生まれつきの才能だけで決まるものではないと考えています。今回は、運動能力を伸ばす考え方と、小松体操教室での取り組みをご紹介します。",
      },
      { type: "heading", text: "考え方:運動神経は「経験の量」で育つ" },
      {
        type: "paragraph",
        text: "運動神経とは、実は特別な才能ではなく、様々な動きを経験した回数によって育っていく能力だといわれています。マット・とび箱・鉄棒など、いろいろな動きに挑戦する経験を積むほど、とっさの場面で体をうまく動かせるようになっていきます。つまり「運動が苦手だから体操教室に向いていない」のではなく、「経験が少ないからこそ体操教室で伸ばせる」というのが小松体操教室の考え方です。",
      },
      { type: "heading", text: "小松体操教室での実践" },
      {
        type: "paragraph",
        text: "小松体操教室のキッズバク転クラスでは、いきなり難しい技に挑戦させるのではなく、マット・とび箱・鉄棒の基礎的な動きを繰り返し経験させることから始めます。基礎ができてくると、クッション・バク転クラスでバク転や宙返りなど、より難しい技にも挑戦できるようになります。「できなかったことができるようになる」経験を積み重ねることで、運動そのものへの自信も育っていきます。",
      },
      {
        type: "paragraph",
        text: "運動神経は、正しい経験の積み重ねで確実に伸ばしていけます。石川県小松市で体操教室をお探しの方は、まずは無料体験でお子さまの様子を見てみてください。",
      },
      {
        type: "cta",
        text: "小松店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=komatsu",
        external: true,
      },
      { type: "cta", text: "小松体操教室の教室ページを見る", href: "/komatsu" },
      {
        type: "link",
        text: "石川県の体操教室まとめ記事も見る",
        href: "/blog/ishikawa-gym-guide-2026",
      },
    ],
    seo: {
      title: "運動神経は生まれつき?伸ばし方｜石川県小松市の体操教室",
      description:
        "「運動神経は生まれつき」と思っていませんか?石川県小松市の小松体操教室(小松店)が、バク転にも挑戦できる運動能力の伸ばし方をご紹介。無料体験レッスン受付中です。",
      keywords: [
        "運動神経 生まれつき",
        "運動能力 伸ばす",
        "小松体操教室",
        "小松市 体操教室",
        "バク転 挑戦",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "ishikawa-hininchi-nouryoku-column-2026",
    title: "あいさつも育つ体操教室｜石川県金沢市鳴和",
    date: "2026年9月7日",
    excerpt:
      "体操教室で育つのは運動能力だけではありません。石川県金沢市鳴和の土日だけ体操教室 鳴和店が、あいさつや礼儀といった非認知能力の育て方をご紹介します。",
    image: Core3,
    imageAlt: "石川県金沢市鳴和の教室で練習前にあいさつをする子どもたち",
    storeId: "ishikawa",
    content: [
      {
        type: "paragraph",
        text: "体操教室というと、跳び箱や鉄棒などの運動能力を伸ばす場所というイメージが強いかもしれません。しかし石川県金沢市鳴和の土日だけ体操教室 鳴和店では、あいさつや礼儀といった、運動以外の力も大切に育てています。今回はその考え方についてご紹介します。",
      },
      { type: "heading", text: "考え方:体操は「あいさつ」から始まる" },
      {
        type: "paragraph",
        text: "体操競技には、演技の前後に礼をする文化があります。この「始まりと終わりのけじめ」を大切にすることは、運動能力とは別の、社会性や礼儀を育てる機会にもなります。鳴和店では、技術指導と同じくらい、教室に来た時・帰る時のあいさつや、順番を守る、友達の練習を応援するといった態度も大切に指導しています。",
      },
      { type: "heading", text: "鳴和店での実践" },
      {
        type: "paragraph",
        text: "鳴和店には年少〜年中の基礎クラスから、中学生・高校生・大人向けの本格的な体幹トレーニングまで、幅広い年齢のお子さまが通っています。年齢の違う生徒同士が同じ教室で顔を合わせる機会もあり、年上の子が年下の子にあいさつやマナーを教える姿も見られます。こうした環境の中で、運動能力だけでなく、集団の中でのふるまい方も自然と身についていきます。",
      },
      {
        type: "paragraph",
        text: "体操教室で育つのは、運動能力だけでなく、あいさつや礼儀といった生きる力の土台でもあります。金沢市鳴和で体操教室をお探しの方は、まずは無料体験にお申し込みください。",
      },
      {
        type: "cta",
        text: "鳴和店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=ishikawa",
        external: true,
      },
      { type: "cta", text: "金沢市城北体操教室の教室ページを見る", href: "/ishikawa" },
      {
        type: "link",
        text: "石川県の体操教室まとめ記事も見る",
        href: "/blog/ishikawa-gym-guide-2026",
      },
    ],
    seo: {
      title: "あいさつも育つ体操教室｜石川県金沢市鳴和",
      description:
        "体操教室で育つのは運動能力だけではありません。石川県金沢市鳴和の土日だけ体操教室 鳴和店が、あいさつや礼儀といった非認知能力の育て方をご紹介。無料体験レッスン受付中です。",
      keywords: [
        "体操教室 非認知能力",
        "あいさつ 礼儀 子ども",
        "金沢市鳴和 体操教室",
        "鳴和店",
        "石川県 体操教室",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "fukui-youji-undou-shuukan-column-2026",
    title: "幼児期に運動習慣をつけるメリット｜福井市の体操教室",
    date: "2026年9月7日",
    excerpt:
      "幼児のうちから運動習慣をつけるメリットを、福井県福井市の土日だけ体操教室 福井店がご紹介。幼児(3歳)から通えるクラスで楽しみながら習慣づくりをサポートします。",
    image: Matto8,
    imageAlt: "福井県福井市の教室で幼児クラスがマット運動を楽しむ様子",
    storeId: "fukui",
    content: [
      {
        type: "paragraph",
        text: "「小さいうちから運動をさせる必要があるの?」と感じる保護者の方もいらっしゃるかもしれません。福井県福井市の土日だけ体操教室 福井店では、幼児(3歳)からのクラスを設けており、早いうちから体を動かす習慣をつけることには多くのメリットがあると考えています。",
      },
      { type: "heading", text: "考え方:幼児期の運動習慣が「その後」を左右する" },
      {
        type: "paragraph",
        text: "幼児期に体を動かす楽しさを知ったお子さまは、その後も運動に前向きに取り組みやすいといわれています。反対に、この時期に運動の機会が少ないと、小学校に上がってから体育の授業に苦手意識を持ってしまうケースも見られます。幼児期の運動習慣は、体力面だけでなく、運動そのものへの気持ちの面でも土台になります。",
      },
      { type: "heading", text: "福井店での実践" },
      {
        type: "paragraph",
        text: "福井体操教室の幼児クラスでは、いきなり技を教え込むのではなく、マット・とび箱・鉄棒・トランポリンといった遊具に触れながら「体を動かすって楽しい」と感じてもらうことを大切にしています。できた・できないで一喜一憂するのではなく、まずは毎週楽しく通えることを目標に、無理のないペースでレッスンを進めています。楽しく通ううちに、自然と基礎的な運動能力も育っていきます。",
      },
      {
        type: "paragraph",
        text: "幼児期の運動習慣づくりは、早すぎるということはありません。福井市で幼児向けの体操教室をお探しなら、まずは無料体験でお子さまの様子をご覧ください。",
      },
      {
        type: "cta",
        text: "福井店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=fukui",
        external: true,
      },
      { type: "cta", text: "福井体操教室の教室ページを見る", href: "/fukui" },
      {
        type: "link",
        text: "福井県の体操教室まとめ記事も見る",
        href: "/blog/fukui-gym-guide-2026",
      },
    ],
    seo: {
      title: "幼児期に運動習慣をつけるメリット｜福井市の体操教室",
      description:
        "幼児のうちから運動習慣をつけるメリットを、福井県福井市の土日だけ体操教室 福井店がご紹介。幼児(3歳)から通えるクラスで楽しみながら習慣づくりをサポートします。無料体験受付中。",
      keywords: [
        "幼児 運動習慣",
        "幼児 体操教室",
        "福井市 体操教室",
        "福井店",
        "運動神経 幼児期",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "mutsukawa-tomobataraki-column-2026",
    title: "土日開催の体操教室という選択｜横浜市南区六ツ川",
    date: "2026年9月7日",
    excerpt:
      "平日の送り迎えが難しい共働き家庭へ。神奈川県横浜市南区六ツ川の土日だけ体操教室 南区六ツ川店が、土日開催だからこそ続けやすい理由をご紹介します。",
    image: Toranporin3,
    imageAlt: "神奈川県横浜市南区六ツ川の教室でトランポリンを楽しむ子ども",
    storeId: "mutsukawa",
    content: [
      {
        type: "paragraph",
        text: "共働き家庭にとって、平日夕方の習い事の送り迎えは大きな負担になりがちです。神奈川県横浜市南区六ツ川の土日だけ体操教室 南区六ツ川店は、その名の通り土日開催に特化した体操教室。今回は、土日開催だからこそ続けやすい理由についてご紹介します。",
      },
      { type: "heading", text: "考え方:「続けやすさ」も習い事選びの大切な基準" },
      {
        type: "paragraph",
        text: "どんなに良い指導内容でも、通い続けられなければ効果は十分に発揮されません。特に共働き家庭では、平日の決まった時間に送迎するのが難しく、習い事を諦めてしまうケースも少なくありません。だからこそ、家庭のスケジュールに無理なく組み込める「続けやすさ」も、習い事選びの重要な基準になります。",
      },
      { type: "heading", text: "六ツ川店での実践" },
      {
        type: "paragraph",
        text: "南区六ツ川店では、リトル・キッズクラスからジュニア、クッションクラスまで、すべて土日の時間帯で開催しています。平日は仕事で送り迎えが難しいご家庭でも、週末なら親子で通いやすく、パパ・ママどちらかが送迎する形でも無理なく続けられます。少人数制のレッスンなので振替にも柔軟に対応しており、急な予定変更があっても通いやすい環境を整えています。",
      },
      {
        type: "paragraph",
        text: "習い事は「続けられること」が何より大切です。横浜市南区六ツ川で土日に通える体操教室をお探しの方は、まずは無料体験にお申し込みください。",
      },
      {
        type: "cta",
        text: "六ツ川店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=yokohama",
        external: true,
      },
      { type: "cta", text: "南区六ツ川体操教室の教室ページを見る", href: "/mutsukawa" },
      {
        type: "link",
        text: "北陸(石川・富山・福井)の体操教室まとめ記事も見る",
        href: "/blog/hokuriku-gym-guide-2026",
      },
    ],
    seo: {
      title: "土日開催の体操教室という選択｜横浜市南区六ツ川",
      description:
        "平日の送り迎えが難しい共働き家庭へ。神奈川県横浜市南区六ツ川の土日だけ体操教室 南区六ツ川店が、土日開催だからこそ続けやすい理由をご紹介。無料体験レッスン受付中です。",
      keywords: [
        "土日開催 体操教室",
        "共働き 習い事",
        "横浜市南区 体操教室",
        "六ツ川店",
        "少人数制 体操教室",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "horikawa-tobibako-kaikyaku-tips-2026",
    title: "跳び箱 開脚跳び上達のコツ｜富山市堀川の体操教室",
    date: "2026年9月21日",
    excerpt:
      "跳び箱の開脚跳びをもっと高い段で決めたい子へ。富山市堀川の土日だけ体操教室 堀川店が、開脚跳びが安定する3つのポイントを紹介します。",
    image: Tobibako2,
    imageAlt: "富山市堀川の教室で跳び箱の開脚跳びに挑戦する子ども",
    storeId: "horikawa",
    content: [
      {
        type: "paragraph",
        text: "「跳び箱は跳べるけど、段数が上がると足が引っかかってしまう」——富山市堀川の土日だけ体操教室 堀川店には、開脚跳びをもっと安定させたい、もっと高い段に挑戦したいというお子さまが数多く通っています。アスリートクラスや選手大会クラスでは跳び箱もより高いレベルを目指しますが、そこに至るまでには共通した基本のコツがあります。今回はその3つのポイントをご紹介します。",
      },
      { type: "heading", text: "開脚跳びでよくある悩み" },
      {
        type: "paragraph",
        text: "堀川店のキッズ・ジュニアクラスでは「踏み切りが弱く跳び箱の手前で失速する」「手をついた後に腰が落ちて足が引っかかる」というつまずきがよく見られます。低い段数では跳べていても、段数が上がるにつれて同じ課題が表面化しやすいのが開脚跳びの特徴です。",
      },
      { type: "heading", text: "練習のポイント3つ" },
      {
        type: "table",
        caption: "開脚跳び上達の3ポイント",
        headers: ["ポイント", "内容"],
        rows: [
          ["① 助走から踏み切りまで加速し続ける", "スピードを落とさず両足で強く踏み切ることで勢いを跳躍力に変える"],
          ["② 手をついたらすぐ突き放す", "手をついた瞬間に肩で体を押し出し、腰が落ちる前に足を開いて通過する"],
          ["③ 着地まで視線を先に向ける", "跳び箱の先を見ることで、体が前のめりになりすぎず安定した着地につながる"],
        ],
      },
      {
        type: "paragraph",
        text: "この3つは低い段数のうちから意識して練習しておくことで、段数が上がったときにも崩れにくいフォームが身につきます。",
      },
      { type: "heading", text: "堀川店でのサポート内容" },
      {
        type: "paragraph",
        text: "堀川店では、ジュニアクラスの基礎練習から、より高い段数やより発展的な技に挑戦するアスリート・選手大会クラスまで、段階を踏んで指導しています。フォームの崩れを一つずつ確認しながら、無理に高い段数へ挑戦させるのではなく、確実にできる段数からステップアップしていくのが堀川店のスタイルです。少人数制だからこそ、一人ひとりの癖に合わせた声かけができます。",
      },
      {
        type: "note",
        text: "堀川店は富山県富山市堀川町355-7 マイプラザ2F（ダイソー前）。土/日開催で、幼児から選手大会クラスまで7つのクラスがあります。",
      },
      {
        type: "cta",
        text: "堀川店の無料体験に申し込む",
        href: "https://free-trial.tht-group.co.jp/",
        external: true,
      },
      { type: "cta", text: "富山市堀川体操教室の教室ページを見る", href: "/horikawa" },
      {
        type: "link",
        text: "富山県の体操教室まとめ記事も見る",
        href: "/blog/toyama-gym-guide-2026",
      },
    ],
    seo: {
      title: "跳び箱 開脚跳び上達のコツ｜富山市堀川の体操教室",
      description:
        "跳び箱の開脚跳びをもっと安定させたいお子さま向けに、富山市堀川の土日だけ体操教室 堀川店が上達の3つのコツを紹介。無料体験レッスン受付中です。",
      keywords: [
        "堀川 跳び箱 開脚跳び",
        "跳び箱 コツ",
        "富山市堀川 体操教室",
        "堀川店",
        "小学生 体操教室",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "takaoka-sokuten-tips-2026",
    title: "マット運動 側転のコツ｜富山県高岡市の体操教室",
    date: "2026年9月21日",
    excerpt:
      "側転がうまく回れない子へ。富山県高岡市の土日だけ体操教室 高岡店が、マット運動の側転が上達する3つのコツを紹介します。無料体験受付中。",
    image: Matto1,
    imageAlt: "富山県高岡市の教室でマット運動の側転に挑戦する子ども",
    storeId: "takaoka",
    content: [
      {
        type: "paragraph",
        text: "「側転をやると横に転がってしまう」「手と足がバラバラになる」——富山県高岡市の土日だけ体操教室 高岡店にも、側転に苦戦するお子さまがよく体験にいらっしゃいます。側転は手と足を順番に使う複雑な動きですが、コツをつかめば誰でも一直線に回れるようになります。",
      },
      { type: "heading", text: "側転でよくある悩み" },
      {
        type: "paragraph",
        text: "高岡体操教室のキッズ・ジュニアクラスでは「体が斜めにねじれてまっすぐ進めない」「手と足のタイミングが合わず途中で止まってしまう」というつまずきがよく見られます。特に「手・手・足・足」の順番を意識せずに勢いだけで回ろうとすると、フォームが崩れやすくなります。",
      },
      { type: "heading", text: "練習のポイント3つ" },
      {
        type: "table",
        caption: "側転上達の3ポイント",
        headers: ["ポイント", "内容"],
        rows: [
          ["① 一直線のラインを意識する", "床にラインを引いたイメージで、手と足を同じ線上につく"],
          ["② 手・手・足・足の順でつく", "四拍子のリズムを声に出しながら練習すると体で覚えやすい"],
          ["③ 腕でしっかり体を支える", "腕を伸ばして体重を支えることで、腰から下が大きく開き安定した回転になる"],
        ],
      },
      { type: "heading", text: "高岡店でのサポート内容" },
      {
        type: "paragraph",
        text: "高岡体操教室では、幼児①・幼児②の段階から体を支える基礎的な運動を取り入れ、キッズ・ジュニアクラスで側転を含むマット運動に本格的に取り組みます。壁を使った練習や、先生が体を支える補助をしながら、正しい手足の順番とタイミングを一つずつ確認していきます。少人数制なので、お子さまのつまずきポイントに合わせた個別の声かけができるのも特徴です。",
      },
      {
        type: "note",
        text: "高岡店は富山県高岡市中川栄町6-10。幼児①・幼児②からクッション・バク転クラスまで、無理なくステップアップできるクラス編成です。",
      },
      {
        type: "cta",
        text: "高岡店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=takaoka",
        external: true,
      },
      { type: "cta", text: "富山県高岡体操教室の教室ページを見る", href: "/takaoka" },
      {
        type: "link",
        text: "富山県の体操教室まとめ記事も見る",
        href: "/blog/toyama-gym-guide-2026",
      },
    ],
    seo: {
      title: "マット運動 側転のコツ｜富山県高岡市の体操教室",
      description:
        "側転が苦手なお子さま向けに、富山県高岡市の土日だけ体操教室 高岡店がマット運動の側転上達のコツを紹介。無料体験レッスン受付中です。",
      keywords: [
        "高岡 側転 コツ",
        "マット運動 側転",
        "高岡市 体操教室",
        "高岡店",
        "小学生 体操教室",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "tonami-toranporin-balance-tips-2026",
    title: "トランポリンでバランス感覚を鍛えるコツ｜砺波市",
    date: "2026年9月21日",
    excerpt:
      "トランポリンで着地が安定しない子へ。富山県砺波市の土日だけ体操教室 砺波店が、バランス感覚が身につく練習のコツを紹介します。",
    image: Toranporin3,
    imageAlt: "富山県砺波市の教室でトランポリンに挑戦する子ども",
    storeId: "tonami",
    content: [
      {
        type: "paragraph",
        text: "「トランポリンで跳ぶと体がグラグラして真っ直ぐ着地できない」——富山県砺波市の土日だけ体操教室 砺波店には、トランポリンでのバランス感覚に不安を感じるお子さまも多く通っています。トランポリンは楽しく跳ぶだけでなく、体幹やバランス感覚を鍛える効果的な種目です。今回は上達のコツをご紹介します。",
      },
      { type: "heading", text: "トランポリンでよくある悩み" },
      {
        type: "paragraph",
        text: "砺波体操教室のキッズ・ジュニアクラスでは「跳ぶたびに違う場所に着地してしまう」「体が反ったり丸まったりして姿勢が保てない」というつまずきがよく見られます。トランポリンの弾む感覚に慣れていないと、無意識に体のバランスを崩してしまいがちです。",
      },
      { type: "heading", text: "練習のポイント3つ" },
      {
        type: "table",
        caption: "バランス感覚上達の3ポイント",
        headers: ["ポイント", "内容"],
        rows: [
          ["① お腹に力を入れて跳ぶ", "体幹を意識することで、跳んでいる間も姿勢が崩れにくくなる"],
          ["② 同じ場所に着地する意識を持つ", "跳ぶたびに真下を見て、同じマス目に着地する練習を繰り返す"],
          ["③ 腕を使ってバランスを取る", "腕を左右に広げることで、体が傾いたときに立て直しやすくなる"],
        ],
      },
      { type: "heading", text: "砺波店でのサポート内容" },
      {
        type: "paragraph",
        text: "砺波体操教室では、幼児①・幼児②の段階から小さくジャンプする感覚に慣れることから始め、キッズ・ジュニアクラスで着地の安定性やジャンプの高さを段階的に伸ばしていきます。トランポリンで養われたバランス感覚は、マット運動や跳び箱など他の種目にも良い影響を与えます。少人数制なので、一人ひとりの跳び方の癖に合わせて丁寧に指導しています。",
      },
      {
        type: "note",
        text: "砺波店は富山県砺波市大辻714。幼児①・幼児②からクッション・バク転クラスまで、無理なくステップアップできるクラス編成です。",
      },
      {
        type: "cta",
        text: "砺波店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=tonami",
        external: true,
      },
      { type: "cta", text: "富山県砺波体操教室の教室ページを見る", href: "/tonami" },
      {
        type: "link",
        text: "富山県の体操教室まとめ記事も見る",
        href: "/blog/toyama-gym-guide-2026",
      },
    ],
    seo: {
      title: "トランポリンでバランス感覚を鍛えるコツ｜砺波市",
      description:
        "トランポリンの着地が安定しないお子さま向けに、富山県砺波市の土日だけ体操教室 砺波店がバランス感覚を鍛えるコツを紹介。無料体験レッスン受付中です。",
      keywords: [
        "砺波 トランポリン",
        "トランポリン バランス感覚",
        "砺波市 体操教室",
        "砺波店",
        "小学生 体操教室",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "komatsu-tetsubou-futonboshi-tips-2026",
    title: "鉄棒「ふとん干し」のコツ｜石川県小松市の体操教室",
    date: "2026年9月21日",
    excerpt:
      "逆上がりの前段階「ふとん干し」ができない子へ。石川県小松市の小松体操教室(小松店)が、鉄棒の基礎技が身につくコツを紹介します。",
    image: Tetubou1,
    imageAlt: "石川県小松市の教室で鉄棒のふとん干しに挑戦する子ども",
    storeId: "komatsu",
    content: [
      {
        type: "paragraph",
        text: "「逆上がりの前に、そもそも鉄棒にぶら下がって体を持ち上げるのが怖い」——石川県小松市の小松体操教室(小松店)には、逆上がりの前段階でつまずくお子さまも多くいらっしゃいます。「ふとん干し」は鉄棒に体を預けて前後に揺れる基礎技で、逆上がりに必要な感覚を養う準備運動として効果的です。今回はそのコツをご紹介します。",
      },
      { type: "heading", text: "ふとん干しでよくある悩み" },
      {
        type: "paragraph",
        text: "小松体操教室のキッズクラスでは「鉄棒に体を預けるのが怖くて力が抜けない」「腕だけで支えようとして疲れてしまう」というつまずきがよく見られます。お腹を鉄棒に乗せて体を預ける感覚に慣れていないと、体が硬直してうまく揺れることができません。",
      },
      { type: "heading", text: "練習のポイント3つ" },
      {
        type: "table",
        caption: "ふとん干し上達の3ポイント",
        headers: ["ポイント", "内容"],
        rows: [
          ["① お腹を鉄棒に乗せる", "腰のあたりを鉄棒に預け、体重を鉄棒に任せる感覚をつかむ"],
          ["② 力を抜いて揺れる", "腕だけでなく体全体の力を抜くことで、自然な振り子のように揺れられる"],
          ["③ 目線を下に向けすぎない", "下ばかり見ず、少し前を見ることで体が丸まりすぎず安定する"],
        ],
      },
      { type: "heading", text: "小松体操教室でのサポート内容" },
      {
        type: "paragraph",
        text: "小松体操教室では、キッズクラスの早い段階からふとん干しなどの基礎技を取り入れ、鉄棒に体を預ける感覚に慣れてもらうことを大切にしています。この感覚が身につくと、キッズバク転クラスやクッション・バク転クラスで挑戦する逆上がりもスムーズに習得しやすくなります。怖がるお子さまには先生が体を支えながら、少しずつ「できた」を積み重ねられるようにしています。",
      },
      {
        type: "note",
        text: "小松体操教室(小松店)は石川県小松市光町47。土/日開催で、幼児5歳から小学生まで通えるクラスがあります。",
      },
      {
        type: "cta",
        text: "小松店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=komatsu",
        external: true,
      },
      { type: "cta", text: "小松体操教室の教室ページを見る", href: "/komatsu" },
      {
        type: "link",
        text: "石川県の体操教室まとめ記事も見る",
        href: "/blog/ishikawa-gym-guide-2026",
      },
    ],
    seo: {
      title: "鉄棒「ふとん干し」のコツ｜石川県小松市の体操教室",
      description:
        "逆上がりの前段階「ふとん干し」が苦手なお子さま向けに、石川県小松市の小松体操教室(小松店)が鉄棒の基礎技のコツを紹介。無料体験レッスン受付中です。",
      keywords: [
        "小松 鉄棒 ふとん干し",
        "鉄棒 基礎技",
        "小松体操教室",
        "小松市 体操教室",
        "幼児 体操教室",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "ishikawa-taikan-training-tips-2026",
    title: "体幹トレーニングの始め方｜石川県金沢市鳴和の体操教室",
    date: "2026年9月21日",
    excerpt:
      "姿勢が崩れやすい子へ。石川県金沢市鳴和の土日だけ体操教室 鳴和店が、体幹トレーニングを始める際の3つのコツを紹介します。",
    image: Core3,
    imageAlt: "石川県金沢市鳴和の教室で体幹トレーニングに取り組む子ども",
    storeId: "ishikawa",
    content: [
      {
        type: "paragraph",
        text: "「姿勢が悪い」「体がフラフラして技が安定しない」——石川県金沢市鳴和の土日だけ体操教室 鳴和店には、体幹の弱さに関するお悩みも多く寄せられます。鳴和店では平日の体幹・体づくりクラスも開講しており、体幹トレーニングを通して姿勢やバランスの改善に取り組んでいます。今回は自宅でも意識できる体幹トレーニングの始め方をご紹介します。",
      },
      { type: "heading", text: "体幹トレーニングでよくある悩み" },
      {
        type: "paragraph",
        text: "鳴和店の体幹・体づくりクラスでは「プランクの姿勢がすぐに崩れてしまう」「体幹を意識するとどこに力を入れればいいかわからない」というお悩みをよく耳にします。体幹は目に見えない筋肉のため、正しい姿勢の感覚をつかむまでに時間がかかりやすいのが特徴です。",
      },
      { type: "heading", text: "練習のポイント3つ" },
      {
        type: "table",
        caption: "体幹トレーニング上達の3ポイント",
        headers: ["ポイント", "内容"],
        rows: [
          ["① 短い時間から始める", "いきなり長く続けようとせず、10〜20秒の正しい姿勢を繰り返すことから始める"],
          ["② お腹とお尻に力を入れる", "体幹はお腹だけでなく、お尻や背中も含めた全体の筋肉を意識する"],
          ["③ 呼吸を止めない", "力むと呼吸が止まりがちだが、自然に呼吸を続けることで長く安定した姿勢を保てる"],
        ],
      },
      { type: "heading", text: "鳴和店でのサポート内容" },
      {
        type: "paragraph",
        text: "鳴和店の体幹・体づくりクラスでは、バランスボールやプランクなどを使いながら、楽しく体幹トレーニングに取り組めるプログラムを用意しています。年少〜年中の基礎クラスから、中学生・高校生・大人向けの本格的な体幹トレーニングまで、年齢に応じて段階的に強度を上げていくのが特徴です。姿勢改善やケガ予防にもつながるため、他のスポーツをがんばるお子さまにもおすすめです。",
      },
      {
        type: "note",
        text: "鳴和店は石川県金沢市鳴和2丁目10-44。体操・トランポリン・体づくり運動から本格的な体幹トレーニングまで幅広く対応しています。",
      },
      {
        type: "cta",
        text: "鳴和店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=ishikawa",
        external: true,
      },
      { type: "cta", text: "金沢市城北体操教室の教室ページを見る", href: "/ishikawa" },
      {
        type: "link",
        text: "石川県の体操教室まとめ記事も見る",
        href: "/blog/ishikawa-gym-guide-2026",
      },
    ],
    seo: {
      title: "体幹トレーニングの始め方｜石川県金沢市鳴和の体操教室",
      description:
        "姿勢が崩れやすいお子さま向けに、石川県金沢市鳴和の土日だけ体操教室 鳴和店が体幹トレーニングを始める際のコツを紹介。無料体験レッスン受付中です。",
      keywords: [
        "鳴和 体幹トレーニング",
        "体幹 姿勢改善",
        "金沢市鳴和 体操教室",
        "鳴和店",
        "石川県 体操教室",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "fukui-tobibako-tips-2026",
    title: "跳び箱のコツ｜福井市の土日だけ体操教室",
    date: "2026年9月21日",
    excerpt:
      "跳び箱が怖くて踏み切れない子へ。福井県福井市の土日だけ体操教室 福井店が、跳び箱が跳べるようになる3つのコツを紹介します。",
    image: Tobibako1,
    imageAlt: "福井県福井市の教室で跳び箱に挑戦する子ども",
    storeId: "fukui",
    content: [
      {
        type: "paragraph",
        text: "「跳び箱の前まで走るけど、直前で止まってしまう」——福井県福井市の土日だけ体操教室 福井店にも、跳び箱に恐怖心を感じるお子さまがよく体験にいらっしゃいます。跳び箱は勢いと踏み切りのタイミングさえつかめれば、多くのお子さまが着実に跳べるようになる種目です。今回は跳び箱上達のコツをご紹介します。",
      },
      { type: "heading", text: "跳び箱でよくある悩み" },
      {
        type: "paragraph",
        text: "福井体操教室のキッズクラスでは「踏み切り板の手前でスピードが落ちてしまう」「跳び箱に手をつくのが怖くて中腰になってしまう」というつまずきがよく見られます。恐怖心があると体が無意識にブレーキをかけてしまい、勢いが失われて跳べなくなってしまいます。",
      },
      { type: "heading", text: "練習のポイント3つ" },
      {
        type: "table",
        caption: "跳び箱上達の3ポイント",
        headers: ["ポイント", "内容"],
        rows: [
          ["① 低い段数から自信をつける", "確実に跳べる低い段数を何度も成功させ、恐怖心を減らしてから段数を上げる"],
          ["② 踏み切り板を両足で強く踏む", "片足ではなく両足同時に強く踏み切ることで体を大きく浮かせられる"],
          ["③ 手を奥について体を持ち上げる", "跳び箱の奥に手をつくことで、お尻が高く上がり跳び越えやすくなる"],
        ],
      },
      { type: "heading", text: "福井店でのサポート内容" },
      {
        type: "paragraph",
        text: "福井体操教室では、幼児クラスから低い段数の跳び箱に親しみ、キッズ・ジュニアクラスで段階的に段数を上げていく指導を行っています。恐怖心が強いお子さまには無理に高い段数に挑戦させず、確実に成功できる高さで「できた」を積み重ねることを大切にしています。少人数制だからこそ、一人ひとりの恐怖心のレベルに合わせた指導が可能です。",
      },
      {
        type: "note",
        text: "福井店は福井県福井市二の宮3丁目42-12 ジョイライフ。幼児(3歳)から通えるクラスがあり、無料体験を受け付けています。",
      },
      {
        type: "cta",
        text: "福井店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=fukui",
        external: true,
      },
      { type: "cta", text: "福井体操教室の教室ページを見る", href: "/fukui" },
      {
        type: "link",
        text: "福井県の体操教室まとめ記事も見る",
        href: "/blog/fukui-gym-guide-2026",
      },
    ],
    seo: {
      title: "跳び箱のコツ｜福井市の土日だけ体操教室",
      description:
        "跳び箱が怖くて踏み切れないお子さま向けに、福井県福井市の土日だけ体操教室 福井店が跳び箱上達の3つのコツを紹介。無料体験レッスン受付中です。",
      keywords: [
        "福井 跳び箱 コツ",
        "跳び箱 練習方法",
        "福井市 体操教室",
        "福井店",
        "幼児 体操教室",
        "体操教室 無料体験",
      ],
    },
  },
  {
    id: "mutsukawa-bakuten-junbi-tips-2026",
    title: "バク転に挑戦する前の準備運動｜横浜市南区六ツ川",
    date: "2026年9月21日",
    excerpt:
      "バク転に挑戦したいけど怖い子へ。横浜市南区六ツ川の土日だけ体操教室 南区六ツ川店が、バク転前に身につけたい準備運動のコツを紹介します。",
    image: Core1,
    imageAlt: "神奈川県横浜市南区六ツ川の教室で体幹トレーニングに取り組む子ども",
    storeId: "mutsukawa",
    content: [
      {
        type: "paragraph",
        text: "「クッションクラスでバク転に挑戦したいけど、後ろに倒れるのが怖い」——神奈川県横浜市南区六ツ川の土日だけ体操教室 南区六ツ川店にも、バク転に憧れつつも恐怖心があるお子さまが多く通っています。バク転はいきなり挑戦する技ではなく、体幹や柔軟性、後ろに倒れる感覚を段階的に養う準備運動が欠かせません。今回はその準備運動のコツをご紹介します。",
      },
      { type: "heading", text: "バク転前でよくある悩み" },
      {
        type: "paragraph",
        text: "南区六ツ川店のクッションクラスでは「後ろに倒れる瞬間に怖くて体が固まってしまう」「手をつく位置がわからず頭から落ちそうで不安」というお悩みをよく聞きます。恐怖心があると体に余計な力が入り、かえって技が不安定になってしまいます。",
      },
      { type: "heading", text: "練習のポイント3つ" },
      {
        type: "table",
        caption: "バク転前の準備運動 3ポイント",
        headers: ["ポイント", "内容"],
        rows: [
          ["① ブリッジで柔軟性を養う", "背中を反らせる感覚に慣れることで、後方回転への恐怖心を減らせる"],
          ["② 体幹トレーニングで軸をつくる", "プランクなどで体幹を鍛えることで、回転中も体がぶれにくくなる"],
          ["③ 補助付きで後方への感覚をつかむ", "先生の補助を受けながら少しずつ後ろに倒れる感覚に慣れていく"],
        ],
      },
      { type: "heading", text: "六ツ川店でのサポート内容" },
      {
        type: "paragraph",
        text: "南区六ツ川店では、体操体幹クラスでプランクやバランス運動などの基礎トレーニングを行い、クッションクラスに進む前に土台となる体幹と柔軟性を養っています。バク転にいきなり挑戦させるのではなく、ブリッジや補助付きの後方倒立など、段階を踏んだ練習で恐怖心を少しずつ取り除いていくのが六ツ川店のスタイルです。",
      },
      {
        type: "note",
        text: "六ツ川店は神奈川県横浜市南区六ツ川4丁目1142-1F 第1笠原ビル。土/日開催で、リトルクラスからクッションクラスまで幅広く対応しています。",
      },
      {
        type: "cta",
        text: "六ツ川店の無料体験に申し込む",
        href: "https://nuts-member.web.app/free_trial/?club=yokohama",
        external: true,
      },
      { type: "cta", text: "南区六ツ川体操教室の教室ページを見る", href: "/mutsukawa" },
      {
        type: "link",
        text: "北陸(石川・富山・福井)の体操教室まとめ記事も見る",
        href: "/blog/hokuriku-gym-guide-2026",
      },
    ],
    seo: {
      title: "バク転に挑戦する前の準備運動｜横浜市南区六ツ川",
      description:
        "バク転に挑戦したいお子さま向けに、横浜市南区六ツ川の土日だけ体操教室 南区六ツ川店が準備運動の3つのコツを紹介。無料体験レッスン受付中です。",
      keywords: [
        "六ツ川 バク転 準備運動",
        "バク転 コツ",
        "横浜市南区 体操教室",
        "六ツ川店",
        "体幹トレーニング",
        "体操教室 無料体験",
      ],
    },
  },
];

const Blog = () => {
  const isMobile = useMediaQuery("(max-width: 700px)");
  const navigate = (path: string) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    window.location.href = path;
  };

  const meta = {
    title: "ブログ - 土日だけ体操教室｜体操教室・バク転・体幹強化",
    description:
      "土日だけ体操教室のブログ。教室からのお知らせ、新クラス情報、地域別の体操教室まとめなどをお届けします。",
    canonical: "https://tht-group.co.jp/blog",
    // title/description/canonical から og:title・og:description・og:url を自動生成
    auto: { ograph: true },
    meta: {
      charset: "utf-8",
      name: {
        keywords: [...commonBlogKeywords, "お知らせ", "バク転", "体幹"].join(","),
        "twitter:card": "summary_large_image",
      },
      property: {
        "og:type": "website",
        "og:site_name": "土日だけ体操教室",
        "og:image": `https://tht-group.co.jp${
          sortPostsByDateDesc(
            blogPosts.filter((post) => !post.storeId || post.storeId === "shared"),
          )[0]?.image
        }`,
      },
    },
  };

  return (
    <>
      <DocumentMeta {...meta}>
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Box sx={{ width: "100vw", overflowX: "hidden" }}>
            <Box
              sx={{
                width: "100%",
                height: isMobile ? "130px" : "300px",
                background: "linear-gradient(to bottom, blue, #00c0ff)",
              }}
            >
              <Grid
                container
                direction="column"
                justifyContent="center"
                alignItems="center"
                sx={{ height: "100%" }}
              >
                <Typography
                  component="h1"
                  variant={isMobile ? "h4" : "h1"}
                  sx={{ color: "#FFC7C7", fontWeight: 700, opacity: 0.3 }}
                >
                  Blog
                </Typography>
                <Typography
                  variant={isMobile ? "h6" : "h3"}
                  sx={{ color: "white", fontWeight: 700 }}
                >
                  ブログ
                </Typography>
              </Grid>
            </Box>
          </Box>
          <Box sx={{ width: isMobile ? "90vw" : "70vw", m: "10vh 0 10vh 0" }}>
            <Grid container direction="column" spacing={4}>
              {sortPostsByDateDesc(
                blogPosts.filter((post) => !post.storeId || post.storeId === "shared"),
              ).map((post) => (
                <Grid
                  item
                  key={post.id}
                  sx={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    gap: isMobile ? 2 : 4,
                    pb: 4,
                    mb: 4,
                    borderBottom: "1px solid #eee",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(`/blog/${post.id}`)}
                >
                  <Box sx={{ width: isMobile ? "100%" : "260px", flexShrink: 0 }}>
                    <Box
                      component="img"
                      src={post.image}
                      alt={post.imageAlt ?? post.title}
                      sx={{
                        width: "100%",
                        height: isMobile ? "180px" : "160px",
                        objectFit: "cover",
                        borderRadius: "12px",
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography sx={{ color: "#999", fontSize: "13px", mb: "6px" }}>
                      {post.date}
                    </Typography>
                    <Typography
                      variant={isMobile ? "subtitle1" : "h6"}
                      sx={{ color: "#333", fontWeight: 700, mb: "10px" }}
                    >
                      {post.title}
                    </Typography>
                    <Typography sx={{ color: "#555", fontSize: "14px" }}>
                      {post.excerpt}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#EBB609",
                        fontWeight: 700,
                        fontSize: "13px",
                        mt: "12px",
                      }}
                    >
                      続きを読む ＞
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </DocumentMeta>
    </>
  );
};

export default Blog;
